import { useState, useRef, useEffect, useMemo } from 'react'
import { CalendarDropdown, CalendarGrid, DayCell, Flexbox, NavButton } from '../Theme/GlobalStyles'
import { AngleLeft, AngleRight, ArrowRight } from '../Theme/Icons'
import { globalTheme } from '../Theme/Theme'
import { Text } from '../Theme/Typography'

const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// `disablePast` greys out days before today and blocks navigating to earlier
// months. Opt-in, because the calendars that only *browse* a date (the Eduspace
// dashboard, DatePicker) legitimately need to look backwards — it's the booking
// flows that must not accept a date that has already gone.
// `maxDate` (Date or 'YYYY-MM-DD', inclusive) is the mirror of `disablePast` at the
// other end: days after it are greyed out and paging forward stops at its month.
// Used by the booking flows to enforce how far ahead a request may be placed.
const CustomCalendar = ({
  selectedDate,
  onSelectDate,
  onClose,
  align = "right",
  disablePast = false,
  maxDate = null,
  maxDateMessage = 'This date is outside the booking window',
}) => {
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(selectedDate ? new Date(selectedDate).getMonth() : today.getMonth());
  const [viewYear, setViewYear] = useState(selectedDate ? new Date(selectedDate).getFullYear() : today.getFullYear());
  const calRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (calRef.current && !calRef.current.contains(e.target)) onClose();
    };
    const handleScroll = () => onClose();
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose]);

  const firstDay = new Date(viewYear, viewMonth, 1);
  const startDay = firstDay.getDay(); // Sunday=0
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startDay; i++) {
    cells.push({ day: null, empty: true });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, empty: false });
  }

  // Midnight today, so today itself stays selectable — comparing raw timestamps
  // would make every earlier hour of today count as past.
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const isPastDay = (day) =>
    disablePast && !!day && new Date(viewYear, viewMonth, day) < todayStart;

  // Normalised to midnight for the same reason as todayStart above: the max day
  // itself must stay selectable.
  const maxDay = useMemo(() => {
    if (!maxDate) return null;
    const d = maxDate instanceof Date ? maxDate : new Date(`${maxDate}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }, [maxDate]);

  const isBeyondMax = (day) =>
    !!maxDay && !!day && new Date(viewYear, viewMonth, day) > maxDay;

  // Nothing before the current month is selectable, so don't let the user page
  // back into a month where every cell is greyed out.
  const atEarliestMonth =
    disablePast && viewYear === today.getFullYear() && viewMonth === today.getMonth();

  // Same idea at the far end — every day past maxDay's month is blocked.
  const atLatestMonth =
    !!maxDay && viewYear === maxDay.getFullYear() && viewMonth === maxDay.getMonth();

  const prevMonth = () => {
    if (atEarliestMonth) return;
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };

  const nextMonth = () => {
    if (atLatestMonth) return;
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const isToday = (day) => day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();
  const isSelected = (day) => {
    if (!selectedDate) return false;
    const sel = new Date(selectedDate);
    return day === sel.getDate() && viewMonth === sel.getMonth() && viewYear === sel.getFullYear();
  };

  const handleSelect = (day) => {
    if (!day || isPastDay(day) || isBeyondMax(day)) return;
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onSelectDate(dateStr);
  };

  const handleClickInside = (e) => {
  e.stopPropagation();
};

  return (
    <CalendarDropdown
      ref={calRef}
      onMouseDown={handleClickInside}
      style={align === "left" ? { left: 0, right: "auto", zIndex: 1000 } : { zIndex: 1000 }}
    >
      <Flexbox spaceBW alignCenter className='mb16'>
        <NavButton
          alignCenter
          justiCenter
          className={atEarliestMonth ? '' : 'pointer'}
          onClick={prevMonth}
          title={atEarliestMonth ? 'Past dates are not available' : undefined}
          style={atEarliestMonth ? { opacity: 0.3, cursor: 'not-allowed' } : undefined}
        >
          <AngleLeft/>
        </NavButton>
        <Text font14 inter medium nuetral13>{MONTHS[viewMonth]} {viewYear}</Text>
        <NavButton
          alignCenter
          justiCenter
          className={atLatestMonth ? '' : 'pointer'}
          onClick={nextMonth}
          title={atLatestMonth ? maxDateMessage : undefined}
          style={atLatestMonth ? { opacity: 0.3, cursor: 'not-allowed' } : undefined}
        >
          <AngleRight/>
        </NavButton>
      </Flexbox>

      <CalendarGrid>
        {DAYS.map(d => (
          <DayCell key={d} empty style={{ cursor: 'default', fontWeight: 600, color: globalTheme.colors.grey2, fontSize: '11px' }}>
            {d}
          </DayCell>
        ))}
        {cells.map((cell, i) => {
          const past = !cell.empty && isPastDay(cell.day);
          const beyond = !cell.empty && isBeyondMax(cell.day);
          const blocked = past || beyond;
          return (
            <DayCell
              key={i}
              empty={cell.empty}
              isToday={!cell.empty && isToday(cell.day)}
              isSelected={!cell.empty && isSelected(cell.day)}
              onClick={() => handleSelect(cell.day)}
              title={past ? 'This date has already passed' : beyond ? maxDateMessage : undefined}
              style={blocked ? { opacity: 0.3, cursor: 'not-allowed' } : undefined}
            >
              {cell.day || ''}
            </DayCell>
          );
        })}
      </CalendarGrid>
    </CalendarDropdown>
  );
};

export default CustomCalendar;
